import { useState, useEffect, useCallback } from 'react';
import { fileService, DocumentAccessRequiredError } from 'shared/services/fileService';
import { useAuth } from 'shared/contexts/AuthContext';

interface ContractorFile {
  id: string;
  filename: string;
  originalName: string;
  fieldName: string;
  documentType: string;
  size: number;
  uploadDate: string;
  downloadUrl: string;
}

interface ContractorFilesData {
  files: ContractorFile[];
  selfieUrl?: string;
  identityFiles: ContractorFile[];
  legalFiles: ContractorFile[];
  medicalFiles: ContractorFile[];
  courseFiles: ContractorFile[];
}

// Helper to check if error is document access required
export const isDocumentAccessRequiredError = (error: any): error is DocumentAccessRequiredError => {
  return error instanceof DocumentAccessRequiredError ||
    (error?.name === 'DocumentAccessRequiredError') ||
    (error?.requiresDocumentAuth === true);
};

export const useContractorFiles = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresDocumentAccess, setRequiresDocumentAccess] = useState(false);
  const [filesData, setFilesData] = useState<ContractorFilesData>({
    files: [],
    identityFiles: [],
    legalFiles: [],
    medicalFiles: [],
    courseFiles: []
  });

  const fetchFiles = useCallback(async () => {

    if (!user) {
      setError('Usuario no autenticado');
      return;
    }

    // Usar id o _id, lo que esté disponible
    const userId = user.id || user._id;
    if (!userId) {
      setError('Error: ID de usuario no disponible');
      return;
    }

    setLoading(true);
    setError(null);
    setRequiresDocumentAccess(false);

    try {

      // Intentar obtener archivos del usuario
      let response;
      try {
        response = await fileService.getContractorFiles(userId);
      } catch (err: any) {

        // Check if document access verification is required
        if (isDocumentAccessRequiredError(err)) {
          setRequiresDocumentAccess(true);
          setFilesData({
            files: [],
            identityFiles: [],
            legalFiles: [],
            medicalFiles: [],
            courseFiles: []
          });
          setError(null);
          setLoading(false);
          return;
        }

        // Si es un error 404, significa que no hay archivos (normal)
        if (err.response?.status === 404) {
          setFilesData({
            files: [],
            identityFiles: [],
            legalFiles: [],
            medicalFiles: [],
            courseFiles: []
          });
          setError(null);
          setLoading(false);
          return;
        }

        // Para error 500, mostrar un mensaje más amigable
        if (err.response?.status === 500) {
          setFilesData({
            files: [],
            identityFiles: [],
            legalFiles: [],
            medicalFiles: [],
            courseFiles: []
          });
          setError('Usuario aun no tiene archivos subidos');
          setLoading(false);
          return;
        }

        // Para otros errores
        setError(err.response?.data?.error?.message || err.message || 'Error al cargar archivos');
        setLoading(false);
        return;
      }
      
      if (response.success && response.files) {
        const files: ContractorFile[] = response.files;
        
        // Separar archivos por documentType y fieldName
        const identityFiles = files.filter(f => f.documentType === 'identity' || ['selfie', 'idFront', 'idBack'].includes(f.fieldName));
        const legalFiles = files.filter(f => f.documentType === 'legal' || ['polizaINS', 'ordenPatronal', 'contractorLicense', 'backgroundCheck'].includes(f.fieldName));
        const medicalFiles = files.filter(f => f.documentType === 'medical' || f.fieldName === 'medicalCertificate');
        const courseFiles = files.filter(f => f.documentType === 'course' || ['initialCourses', 'additionalCourses'].includes(f.fieldName));
        
        
        // Buscar el selfie para el avatar
        const selfieFile = files.find(f => f.fieldName === 'selfie');
        let selfieUrl = undefined;
        
        if (selfieFile) {
          try {
            const blob = await fileService.downloadContractorFile(selfieFile.id);
            selfieUrl = URL.createObjectURL(blob);
          } catch (err: unknown) {
          }
        } else {
        }

        const finalFilesData = {
          files,
          selfieUrl,
          identityFiles,
          legalFiles,
          medicalFiles,
          courseFiles
        };
        
        
        setFilesData(finalFilesData);
      } else {
        setError(response.message || 'Error al obtener archivos');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar los archivos');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const downloadFile = async (fileId: string, originalName: string) => {
    try {

      const blob = await fileService.downloadContractorFile(fileId);

      // Crear enlace de descarga
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {

      // Check if document access verification is required
      if (isDocumentAccessRequiredError(err)) {
        setRequiresDocumentAccess(true);
        throw err; // Re-throw so caller can handle the dialog
      }

      throw new Error(`Error al descargar el archivo: ${err.response?.data?.error || err.message}`);
    }
  };

  // Function to clear document access requirement (call after successful verification)
  const clearDocumentAccessRequirement = useCallback(() => {
    setRequiresDocumentAccess(false);
  }, []);

  const deleteFile = async (fileId: string) => {
    try {
      const response = await fileService.deleteContractorFile(fileId);
      if (response.success) {
        // Recargar archivos después de eliminar
        await fetchFiles();
      } else {
        throw new Error(response.message || 'Error al eliminar archivo');
      }
    } catch (err: any) {
      throw new Error('Error al eliminar el archivo');
    }
  };

  // Limpiar URLs del blob cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (filesData.selfieUrl) {
        URL.revokeObjectURL(filesData.selfieUrl);
      }
    };
  }, [filesData.selfieUrl]);

  useEffect(() => {
    if (user?.id) {
      fetchFiles();
    }
  }, [user?.id, fetchFiles]);

  return {
    loading,
    error,
    filesData,
    fetchFiles,
    downloadFile,
    deleteFile,
    refetch: fetchFiles,
    requiresDocumentAccess,
    clearDocumentAccessRequirement
  };
};